import { store } from '../../store/store';
import { cleanupAllCollaborationListeners } from '../../store/collaborationThunks';
import {
  getActiveListenerCount,
  getActiveListenerIds,
  logListenerStatus,
  cleanupAllListeners,
} from './firestoreListeners';
import { debugLog, debugWarn, isDebugEnabled } from '../../utils/debug';

/**
 * Browser Integration for Collaboration Features
 *
 * Handles browser-level cleanup and development debugging for real-time collaboration.
 * Called from index.tsx to set up global cleanup and debug helpers.
 */

/**
 * Global cleanup function for page unload events
 * Prevents memory leaks when user closes tab or navigates away
 */
export const handlePageUnload = () => {
  debugLog('[BrowserIntegration] Page unloading, cleaning up collaboration listeners');
  store.dispatch(cleanupAllCollaborationListeners());
};

/**
 * Sets up browser event listeners for collaboration cleanup
 * Should be called once during app initialization
 */
export const setupBrowserCleanup = () => {
  // Listen for page unload events to cleanup listeners
  window.addEventListener('beforeunload', handlePageUnload);
  window.addEventListener('pagehide', handlePageUnload);

  debugLog('[BrowserIntegration] Browser cleanup listeners registered');
};

/**
 * Development debug helpers for testing collaboration functionality
 * Only available in development mode
 */
export const createDebugHelpers = () => {
  if (process.env.NODE_ENV !== 'development') {
    return null;
  }

  const debugHelpers = {
    // Redux-based helpers (accurate count)
    getReduxListenerCount: () => store.getState().map3Data.activeListenerIds.length,
    getReduxListenerIds: () => store.getState().map3Data.activeListenerIds,

    // Service-based helpers (for comparison)
    getServiceListenerCount: getActiveListenerCount,
    getServiceListenerIds: getActiveListenerIds,

    // Status logging
    logStatus: () => {
      const reduxIds = store.getState().map3Data.activeListenerIds;
      const serviceIds = getActiveListenerIds();
      debugLog('🔧 Collaboration Status:');
      debugLog('  Redux listener IDs:', reduxIds);
      debugLog('  Service listener IDs:', serviceIds);
      debugLog('  Redux count:', reduxIds.length);
      debugLog('  Service count:', serviceIds.length);
      debugLog('  Connection status:', store.getState().map3Data.connectionStatus);
    },

    // Manual cleanup helpers
    cleanup: () => {
      debugLog('[Debug] Manual cleanup triggered');
      cleanupAllListeners();
    },

    emergencyCleanup: () => {
      debugLog('[Debug] Emergency cleanup triggered');
      store.dispatch(cleanupAllCollaborationListeners());
    },
  };

  return debugHelpers;
};

/**
 * Sets up development debug helpers on window object
 * Only available in development mode
 */
export const setupDebugHelpers = () => {
  if (process.env.NODE_ENV !== 'development') {
    return;
  }

  const debugHelpers = createDebugHelpers();
  if (debugHelpers) {
    window.collaborationDebug = debugHelpers;

    debugLog('🔧 Collaboration Debug helpers available:');
    debugLog('- window.collaborationDebug.getReduxListenerCount() - Get Redux listener count');
    debugLog('- window.collaborationDebug.getReduxListenerIds() - Get Redux listener IDs');
    debugLog('- window.collaborationDebug.getServiceListenerCount() - Get service listener count');
    debugLog('- window.collaborationDebug.logStatus() - Log detailed status');
    debugLog('- window.collaborationDebug.cleanup() - Manual cleanup');
    debugLog('- window.collaborationDebug.emergencyCleanup() - Emergency cleanup');
  }
};

/**
 * Main initialization function for collaboration browser integration
 * Call this once from index.tsx during app startup
 */
export const initializeCollaborationBrowserIntegration = () => {
  setupBrowserCleanup();
  setupDebugHelpers();

  debugLog('[BrowserIntegration] Collaboration browser integration initialized');
};
